import { DateObject, getAllDatesInRange } from "react-multi-date-picker";

import ApiService from "../../../../services/ApiService";
import { convertDateFormat, formatDateHumanly, getAgeHumanly, getDays } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";

const _getDayOfWeek = (dateStr: string, language: string): string => {
  const [day, month, year] = dateStr.split("/").map((num) => parseInt(num, 10));
  const date = new Date(year, month - 1, day);
  const dayOfWeek = date.getDay();

  return getDays(language)[dayOfWeek];
};

export const fetchUserLogs = (
  user_id: number,
  setUserLogs: Function,
  setLoadingUserLogs: Function,
  language: string,
  limit?: number
) => {
  setLoadingUserLogs(true);
  ApiService.get(
    `users/${user_id}/logs?type_exclude=notebook_time,meeting_edited&order=desc${limit ? `&limit=${limit}` : ""}`
  )
    .then((response) => {
      const logs = response.data.logs.map((log: any) => ({
        ...log,
        date: getAgeHumanly(language, log.timestamp, true),
      }));
      setUserLogs(logs);
    })
    .catch((error) => console.error("An error occcured while fetching user logs", error))
    .finally(() => setLoadingUserLogs(false));
};

export const fetchUserLogsDetailed = (
  user_id: number,
  setUserLogs: Function,
  setLoadingUserLogs: Function,
  language: string,
  limit?: number
) => {
  setLoadingUserLogs(true);
  ApiService.get(
    `users/${user_id}/logs/detailed?type_exclude=notebook_time,meeting_edited&order=desc${
      limit ? `&limit=${limit}` : ""
    }`
  )
    .then((response) => {
      setUserLogs(
        response.data.logs.map((log: any) => ({
          ...log,
          date: getAgeHumanly(language, log.timestamp, true),
        }))
      );
    })
    .catch((error) => {
      console.error("Error occurred while fetching details on user logs", error);
      alert(translate(language, "COMPONENTS.USER_ACTIVITY.ERROR.FETCH_DETAILED_LOGS"));
    })
    .finally(() => setLoadingUserLogs(false));
};

export const fetchUserLogTimeCsv = (user_id: number, username: string, setIsLoadingCsv: Function, months?: number) => {
  setIsLoadingCsv(true);
  ApiService.get(`users/${user_id}/logtime/csv${months ? `?months=${months}` : ""}`)
    .then((response) => {
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(new Blob([response.data]));
      link.setAttribute(
        "download",
        months ? `logtime_last_${months}_months_${username}.csv` : `logtime_${username}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    })
    .catch((error) => console.error("An error occcured while fetching user logtime in CSV", error))
    .finally(() => setIsLoadingCsv(false));
};

export const fetchUserLogTime = (user_id: number, setUserLogTime: Function, setLoadingLogtime: Function) => {
  setLoadingLogtime(true);
  ApiService.get(`users/${user_id}/logtime`)
    .then((response) => {
      if (response.data.success && typeof response.data.logtime === "object") {
        setUserLogTime(response.data.logtime);
      } else {
        console.error("Unexpected response structure:", response.data);
      }
    })
    .catch((error) => console.error("An error occcured while fetching user logtime", error))
    .finally(() => setLoadingLogtime(false));
};

const fetchUserCohortsLogTime = (userCohortsId: any, ts_start: number, ts_end: number, setIsLoading: Function) => {
  setIsLoading(true);
  return new Promise((resolve, _reject) => {
    ApiService.get(`cohorts/${userCohortsId}/logtime/average?ts_start=${ts_start}&ts_end=${ts_end}`)
      .then((response) => {
        if (response.data.success) {
          resolve(response.data.average_logtime || {});
        } else {
          console.error("No success in response:", response.data);
          resolve({});
        }
      })
      .catch((error) => {
        console.error("An error occcured while fetching user cohort log time", error);
        resolve({});
      })
      .finally(() => setIsLoading(false));
  });
};

export const handleDateChange = async (
  dateObjects: DateObject[],
  language: string,
  userName: string,
  selectedCohortId: number,
  cohort: any,
  setBarConfig: Function,
  userLogTime: any,
  setGraphData: Function,
  setIsDateRangeSelected: Function,
  setIsLoading: Function,
  setLegendTitle: Function
) => {
  const datesArray = dateObjects.map((date) => date.toDate());

  const allDatesArray = getAllDatesInRange(dateObjects as any);

  if (allDatesArray.length >= 1 && datesArray.length === 2) {
    let start;
    let end;
    if (allDatesArray.length === 1) {
      start = datesArray[0].getTime();
      end = datesArray[0].getTime();
    } else {
      start = datesArray[0].getTime();
      end = datesArray[1].getTime();
    }
    let ts_start = Math.floor(start / 1000);
    let ts_end = Math.floor(end / 1000);
    let cohortLogtime: any = {};
    if (selectedCohortId !== -1) {
      cohortLogtime = await fetchUserCohortsLogTime(selectedCohortId, ts_start, ts_end, setIsLoading);
      setBarConfig([{ dataKey: userName }, { dataKey: cohort.name.toString() }]);
    } else {
      setBarConfig([{ dataKey: userName }]);
    }

    const newGraphData = allDatesArray.map((date) => {
      const dateKey = convertDateFormat(date.toString());
      const data: any = {
        name: dateKey,
        [userName]: userLogTime[dateKey as any] || 0,
        day: _getDayOfWeek(dateKey, language),
      };
      if (selectedCohortId !== -1) {
        data[cohort.name] = (cohortLogtime as any)[dateKey as any] || 0;
        data.cohort = cohort.name;
        data.cohortValue =
          (~~(cohortLogtime as any)[dateKey as any] < 1 ? "<1 min" : ~~(cohortLogtime as any)[dateKey as any]) || 0;
      }
      return data;
    });

    setGraphData([...newGraphData]);
    setIsDateRangeSelected(true);
    if (allDatesArray.length === 1) {
      setLegendTitle(formatDateHumanly(language, dateObjects[0].toString()));
    } else {
      setLegendTitle(
        `${formatDateHumanly(language, dateObjects[0].toString())} - ${formatDateHumanly(
          language,
          dateObjects[1].toString()
        )}`
      );
    }
  }
};

export const filterCohortModules = (cohorts: any[], cohortId?: number) => {
  return (
    cohorts
      ?.filter((module: any) => (cohortId ? module.sprint_cohort_id === cohortId : true))
      .flatMap((cohort: any) =>
        cohort.modules.map((moduleItem: any) => ({
          label: moduleItem.module.name,
          value: moduleItem.module.user_time_spent || 0,
        }))
      ) || []
  );
};

export const filterAllUserModules = (userModules: any[], userModulesCohort: any[]) => {
  const allModules = [
    ...(userModules?.map((module: any) => ({
      label: module.module.name,
      value: module.user_time_spent || 0,
    })) || []),
    ...filterCohortModules(userModulesCohort),
  ];

  const moduleMap = new Map();
  allModules.forEach((module: any) => {
    !moduleMap.has(module.label) && moduleMap.set(module.label, { ...module });
  });

  return Array.from(moduleMap.values());
};
